import { Action, createReducer, on } from "@ngrx/store";
import * as FuelStocksActionTypes from "../../actions/fuelStocks/fuelStocks.actions";
import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";
import {
  CustomInventoryDTO,
  InventoryMovementSimpleDTO,
  InventoryStockExtendedDTO,
} from "src/app/models/fuelStocks/fuelStocksDTO";

export interface FuelStocksState {
  fuelStockList?: CustomInventoryDTO[];
  fuelStockExcelList?: CustomInventoryDTO[];
  fuelStockMovements?: any;
  totalFuelStocks?: number;
  isLoading: boolean;
  error: Error;
  created: boolean;
  fuelStock: InventoryStockExtendedDTO;
  totalRecords: number;
  pageReq: PaginationRequest;
  lastMeasurement?: InventoryMovementSimpleDTO;
  lastDelivery?: InventoryMovementSimpleDTO;
  inventoryStats?: any;
  fuelStockTableConfig: ColumnSorterInfo[];
  fuelStockMovementsTableConfig: ColumnSorterInfo[];
}

export const initialState: FuelStocksState = {
  fuelStockList: null,
  fuelStockMovements: null,
  isLoading: false,
  error: null,
  created: false,
  fuelStock: null,
  totalRecords: 0,
  pageReq: null,
  lastMeasurement: null,
  lastDelivery: null,
  inventoryStats: null,
  fuelStockTableConfig: null,
  fuelStockMovementsTableConfig: null,
};

const fuelStocksReducer = createReducer(
  initialState,
  on(FuelStocksActionTypes.resetFuelStocksState, () => initialState),
  on(FuelStocksActionTypes.getFuelStocks, (state, { options }) => {
    return {
      ...state,
      options: options,
      isLoading: true,
    };
  }),
  on(FuelStocksActionTypes.loadFuelStocks, (state, { response }) => {
    return {
      ...state,
      fuelStockList: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      error: null,
      isLoading: false,
      totalFuelStocks: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(
    FuelStocksActionTypes.getLastMeasurements,
    (state, { code }) =>
      <FuelStocksState>{
        ...state,
        error: null,
        code: code,
      }
  ),
  on(FuelStocksActionTypes.setLastMeasurements, (state, { result }) => ({
    ...state,
    lastMeasurement: (result as any).result
      ? (result as any).result
      : undefined,
    error: null,
  })),
  on(
    FuelStocksActionTypes.getLastDeliveries,
    (state, { code }) =>
      <FuelStocksState>{
        ...state,
        error: null,
        code: code,
      }
  ),
  on(FuelStocksActionTypes.setLastDeliveries, (state, { result }) => ({
    ...state,
    lastDelivery: (result as any).result ? (result as any).result : undefined,
    error: null,
  })),
  on(FuelStocksActionTypes.getFuelStock, (state, { fuelStock }) => {
    return {
      ...state,
      action: fuelStock,
      created: false,
      isLoading: true,
    };
  }),
  on(FuelStocksActionTypes.setFuelStock, (state, { response }) => ({
    ...state,
    fuelStock: (response as any).result ? (response as any).result : undefined,
    isLoading: false,
    error: null,
  })),
  on(FuelStocksActionTypes.loadFuelStocksLoadStart, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(FuelStocksActionTypes.loadFuelStocksLoadEnd, (state) => ({
    ...state,
    isLoading: false,
    error: null,
  })),
  on(FuelStocksActionTypes.resetFuelStock, (state) => {
    return {
      ...state,
      fuelStock: null,
      error: null,
    };
  }),
  on(FuelStocksActionTypes.loadInventoryStats, (state, { result }) => ({
    ...state,
    isLoading: false,
    inventoryStats: (result as any).result ? (result as any).result : undefined,
    error: null,
  })),
  on(
    FuelStocksActionTypes.getFuelStockExcelList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as FuelStocksState)
  ),
  on(FuelStocksActionTypes.loadFuelStockExcelList, (state, { response }) => {
    return {
      ...state,
      fuelStockExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(FuelStocksActionTypes.loadFuelStockTableConfig, (state, { response }) => {
    return {
      ...state,
      fuelStockTableConfig:
        (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(
    FuelStocksActionTypes.loadFuelStockMovementsTableConfig,
    (state, { response }) => {
      return {
        ...state,
        fuelStockMovementsTableConfig:
          (response as any)?.result?.value?.tableColumnInfo ?? undefined,
      };
    }
  ),

  on(FuelStocksActionTypes.setLoadingEndState, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  on(FuelStocksActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    FuelStocksActionTypes.createFuelStock,
    FuelStocksActionTypes.updateFuelStock,
    FuelStocksActionTypes.saveFuelStockTableConfig,
    FuelStocksActionTypes.getFuelStockTableConfig,
    FuelStocksActionTypes.saveFuelStockMovementsTableConfig,
    FuelStocksActionTypes.getFuelStockMovementsTableConfig,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }
  ),
  on(FuelStocksActionTypes.getFuelStockMovements, (state, { options }) => {
    return {
      ...state,
      options: options,
      error: null,
      isLoading: true,
    } as FuelStocksState;
  }),
  on(FuelStocksActionTypes.loadFuelStockMovements, (state, { response }) => {
    return {
      ...state,
      fuelStockMovements: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      error: null,
      isLoading: false,
    };
  })
);

export function reducer(state: FuelStocksState | undefined, action: Action) {
  return fuelStocksReducer(state, action);
}
